<script setup lang="ts">
export interface BaseHeaderLinksProps {
  links: IHeaderLink[];
  leftOffset?: number;
  rightOffset?: number;
}

const props = withDefaults(defineProps<BaseHeaderLinksProps>(), {
  leftOffset: 0,
  rightOffset: 0,
});

const visibleLinks = ref<IHeaderLink[]>([]);
const invisibleLinks = ref<IHeaderLink[]>([]);

const linkWidthCache = new Map<string, number>();
let measurementSpan: HTMLSpanElement | null = null;

const getMeasurementSpan = () => {
  if (!measurementSpan) {
    measurementSpan = document.createElement("span");
    measurementSpan.style.visibility = "hidden";
    measurementSpan.style.position = "absolute";
    measurementSpan.style.fontWeight = "500";
    measurementSpan.style.whiteSpace = "nowrap";
    document.body.appendChild(measurementSpan);
  }
  return measurementSpan;
};

const getLinkWidth = (title: string): number => {
  if (linkWidthCache.has(title))
    return linkWidthCache.get(title)!;

  const span = getMeasurementSpan();
  span.textContent = title;
  const width = Math.min(span.offsetWidth, 144) + 48;

  linkWidthCache.set(title, width);
  return width;
};

const calculateVisibleLinks = () => {
  if (!props.links.length || !import.meta.client)
    return;

  let totalWidth = 0;
  const maxWidth = window.innerWidth - props.rightOffset - props.leftOffset - 100;

  const newVisibleLinks: IHeaderLink[] = [];
  const newInvisibleLinks: IHeaderLink[] = [];

  for (const link of props.links) {
    const linkWidth = getLinkWidth(link.title);
    totalWidth += linkWidth;

    if (totalWidth <= maxWidth)
      newVisibleLinks.push(link);
    else
      newInvisibleLinks.push(link);
  }

  visibleLinks.value = newVisibleLinks;
  invisibleLinks.value = newInvisibleLinks;
};

let resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
const debouncedResize = () => {
  if (resizeTimeoutId)
    clearTimeout(resizeTimeoutId);

  resizeTimeoutId = setTimeout(() => {
    calculateVisibleLinks();
  }, 150);
};

watch(() => [props.links, props.leftOffset, props.rightOffset] as const, () => {
  linkWidthCache.clear();
  calculateVisibleLinks();
}, { immediate: true, deep: true });

onMounted(() => {
  if (import.meta.client) {
    window.addEventListener("resize", debouncedResize);
    calculateVisibleLinks();
  }
});

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener("resize", debouncedResize);

    if (resizeTimeoutId)
      clearTimeout(resizeTimeoutId);

    if (measurementSpan && measurementSpan.parentNode)
      measurementSpan.parentNode.removeChild(measurementSpan);

    measurementSpan = null;
    linkWidthCache.clear();
  }
});
</script>

<template>
  <div class="flex items-center">
    <BaseHeaderVisibleLinks :links="visibleLinks" />
    <BaseHeaderInvisibleLinks class="mt-2 hidden lg:block" :links="invisibleLinks" />
  </div>
</template>
